import React, { useState } from "react";
import { useGame } from "@/contexts/GameContext";
import { Button } from "@/components/ui/button";
import { MapPin, Loader2, RefreshCw, Navigation } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/utils/i18n";
import { resolveImageUrl } from "@/utils/imageUrl";
import useTabMusic from "@/hooks/useTabMusic";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WorldMap() {
  const { game, generateMapImage } = useGame();
  const [generating, setGenerating] = useState(false);
  useTabMusic('map');  // Iter 104 — per-section music

  if (!game) return null;

  const lang = game.language || "en";
  const mapData = game.map_data || { locations: [], current_location: null };
  const locations = mapData.locations || [];
  const currentLocation = mapData.current_location;
  const mapImageUrl = mapData.image_url;

  const handleGenerateMap = async () => {
    if (locations.length === 0) {
      toast.error(t(lang, 'explore_more'));
      return;
    }
    setGenerating(true);
    try {
      await generateMapImage(game.id);
      toast.success(t(lang, 'world_map') + "!");
    } catch {
      toast.error("Failed to generate map");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="py-3 sm:py-6 px-2 sm:px-4" data-testid="world-map">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
          <h2 className="font-heading text-base sm:text-xl text-[#cfb53b] flex items-center gap-2">
            <Navigation size={20} /> {t(lang, 'world_map')}
          </h2>
          <Button
            data-testid="generate-map-button"
            onClick={handleGenerateMap}
            disabled={generating || locations.length === 0}
            variant="outline"
            size="sm"
            className="border-[#cfb53b]/30 text-[#cfb53b] hover:bg-[#cfb53b]/10 text-xs sm:text-sm"
          >
            {generating ? <Loader2 className="animate-spin mr-2" size={14} /> : <RefreshCw size={14} className="mr-2" />}
            {t(lang, 'generate_map')}
          </Button>
        </div>

        {mapImageUrl && (
          <div className="map-container rounded-lg overflow-hidden mb-6" data-testid="map-image">
            <img
              src={resolveImageUrl(mapImageUrl, BACKEND_URL)}
              alt="World Map"
              className="w-full max-h-[400px] object-contain filter sepia-[0.3] contrast-[1.1]"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        {locations.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#cfb53b]/20 rounded-lg">
            <MapPin size={48} className="mx-auto text-[#cfb53b]/20 mb-4" />
            <p className="text-[#94a3b8]">{t(lang, 'no_locations')}</p>
            <p className="text-[#64748b] text-sm mt-1">{t(lang, 'explore_more')}</p>
          </div>
        ) : (
          <div className="space-y-3 stagger-children" data-testid="locations-list">
            {locations.map((loc, idx) => {
              const isCurrent = loc.name === currentLocation;
              return (
                <div
                  key={idx}
                  data-testid={`location-${idx}`}
                  className={`map-location flex items-center gap-4 bg-[#1a1a1d]/80 border rounded-lg p-4 animate-fadeInUp ${
                    isCurrent
                      ? 'border-[#cfb53b]/40 bg-[#cfb53b]/5 current'
                      : 'border-[#2d3748]'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    isCurrent ? 'bg-[#cfb53b]/20' : 'bg-[#2d3748]'
                  }`}>
                    <MapPin size={18} className={isCurrent ? 'text-[#cfb53b]' : 'text-[#64748b]'} />
                  </div>
                  <div>
                    <h4 className={`font-heading text-sm ${isCurrent ? 'text-[#cfb53b]' : 'text-[#e2e8f0]'}`}>
                      {loc.name}
                      {isCurrent && <span className="ml-2 text-xs bg-[#cfb53b]/20 text-[#cfb53b] px-2 py-0.5 rounded-full">{t(lang, 'current_location')}</span>}
                    </h4>
                    <p className="text-xs text-[#94a3b8]">{loc.description}</p>
                  </div>
                  <div className="ml-auto text-xs text-[#64748b]">
                    #{idx + 1}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
